import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  UPDATE_CONFIG,
  UPDATE_STATE,
  UpdateState,
  VersionPayload,
} from '../../constants/update.constants';

@Injectable({
  providedIn: 'root',
})
export class UpdateService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly _state$ = new BehaviorSubject<UpdateState>(UPDATE_STATE.IDLE);
  private readonly _remainingMs$ = new BehaviorSubject<number>(0);
  private readonly _latestVersion$ = new BehaviorSubject<VersionPayload | null>(
    null,
  );

  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private started = false;

  public readonly state$: Observable<UpdateState> = this._state$
    .asObservable()
    .pipe(distinctUntilChanged());

  public readonly remainingMs$: Observable<number> = this._remainingMs$
    .asObservable()
    .pipe(distinctUntilChanged());

  public readonly latestVersion$: Observable<VersionPayload | null> =
    this._latestVersion$.asObservable();

  constructor(private http: HttpClient) {}

  public start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    timer(UPDATE_CONFIG.INITIAL_DELAY_MS, UPDATE_CONFIG.POLL_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.fetchVersion()),
        filter((payload): payload is VersionPayload => payload !== null),
      )
      .subscribe((payload) => this.handleVersion(payload));
  }

  public forceBlocking(): void {
    this.clearCountdown();
    this._remainingMs$.next(0);
    this._state$.next(UPDATE_STATE.BLOCKING);
  }

  public acceptVersion(version: string): void {
    this.safeSet(UPDATE_CONFIG.ACCEPTED_VERSION_KEY, version);
    this.safeRemove(UPDATE_CONFIG.DETECTED_VERSION_KEY);
    this.safeRemove(UPDATE_CONFIG.DETECTED_AT_KEY);
  }

  public getCurrent(): VersionPayload | null {
    return this._latestVersion$.value;
  }

  private fetchVersion(): Observable<VersionPayload | null> {
    const url = `${UPDATE_CONFIG.VERSION_URL}?t=${Date.now()}`;
    return this.http
      .get<VersionPayload>(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      })
      .pipe(catchError(() => of(null)));
  }

  private handleVersion(payload: VersionPayload): void {
    if (!payload?.version) {
      return;
    }

    this._latestVersion$.next(payload);

    const accepted = this.safeGet(UPDATE_CONFIG.ACCEPTED_VERSION_KEY);

    if (accepted === null) {
      this.acceptVersion(payload.version);
      return;
    }

    if (accepted === payload.version) {
      if (this._state$.value !== UPDATE_STATE.IDLE) {
        this.clearCountdown();
        this._state$.next(UPDATE_STATE.IDLE);
      }
      return;
    }

    this.ensureCountdownFor(payload.version);
  }

  private ensureCountdownFor(version: string): void {
    const storedVersion = this.safeGet(UPDATE_CONFIG.DETECTED_VERSION_KEY);
    const storedAtRaw = this.safeGet(UPDATE_CONFIG.DETECTED_AT_KEY);
    const storedAt = storedAtRaw ? Number(storedAtRaw) : NaN;

    const validStored =
      storedVersion === version && Number.isFinite(storedAt) && storedAt > 0;

    const detectedAt = validStored ? storedAt : Date.now();

    if (!validStored) {
      this.safeSet(UPDATE_CONFIG.DETECTED_VERSION_KEY, version);
      this.safeSet(UPDATE_CONFIG.DETECTED_AT_KEY, String(detectedAt));
    }

    const elapsed = Date.now() - detectedAt;
    const remaining = UPDATE_CONFIG.WARNING_DURATION_MS - elapsed;

    if (remaining <= 0) {
      this.forceBlocking();
      return;
    }

    this._remainingMs$.next(remaining);
    if (this._state$.value !== UPDATE_STATE.WARNING) {
      this._state$.next(UPDATE_STATE.WARNING);
    }
    this.startCountdownTicker(detectedAt);
  }

  private startCountdownTicker(detectedAt: number): void {
    this.clearCountdown();
    this.countdownInterval = setInterval(() => {
      const remaining =
        UPDATE_CONFIG.WARNING_DURATION_MS - (Date.now() - detectedAt);
      if (remaining <= 0) {
        this.forceBlocking();
        return;
      }
      this._remainingMs$.next(remaining);
    }, UPDATE_CONFIG.COUNTDOWN_TICK_MS);
  }

  private clearCountdown(): void {
    if (this.countdownInterval !== null) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private safeGet(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeSet(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      return;
    }
  }

  private safeRemove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      return;
    }
  }

  ngOnDestroy(): void {
    this.clearCountdown();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
